import React, { useState, useRef, useMemo } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon as MenuItemIcon,
  Drawer,
} from "@mui/material";
import {
  CloudUpload,
  InsertDriveFile,
  CheckCircle,
  Error as ErrorIcon,
  FolderOpen,
  ChevronRight as ChevronRightIcon,
  ArrowDropDown,
  Visibility,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

import { uploadFiles } from "../api/agentApi";
import { v4 as uuidv4 } from 'uuid';

interface UploadedFile {
  filename: string;
  path: string;
  size: number;
}

interface UploadResponse {
  uploaded: UploadedFile[];
  count: number;
  errors?: string[];
}

// For the drawer showing files uploaded and their status
interface FileWithStatus {
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
  relativePath?: string;
  id: string;
  uploadSessionId?: string; // To track which upload session this file belongs to
  isCurrentSession?: boolean; // To distinguish current vs previous uploads
}

interface Props {
  setUploadedFiles: (resp: any) => void;
  uploadId?: string; // Optional upload ID to reuse for same index
  onUploadIdCreated?: (uploadId: string) => void; // Callback to notify parent of generated upload ID
}

const RAGFileUploader: React.FC<Props> = (props) => {
  const [files, setFiles] = useState<FileWithStatus[]>([]); // Cumulative list of all files
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null); // Track current upload session
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // The browser enumerates and processes all selected files before firing onChange event for <input>
  // So a loadingFiles state is needed to show spinner while waiting for user to select files/folder
  const [loadingFiles, setLoadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  // Ref for timeout ID handling Cancel
  const cancellationTimeoutRef = useRef<any>(null);

  // clear the cancellation timeout
  const clearCancellationTimeout = () => {
    if (cancellationTimeoutRef.current) {
      clearTimeout(cancellationTimeoutRef.current);
      cancellationTimeoutRef.current = null;
    }
  };

  // unique ID for each file item
  const generateFileId = (file: File) =>
    `${file.name}-${file.size}-${Date.now()}`;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSelectAndUploadFiles = () => {
    handleMenuClose();

    setLoadingFiles(true);
    clearCancellationTimeout();

    // timeout (500ms) to handle cancel
    cancellationTimeoutRef.current = setTimeout(() => {
      // This runs only if onChange NOT triggered (i.e., user canceled the dialog)
      setLoadingFiles(false);
      cancellationTimeoutRef.current = null;
    }, 500);

    fileInputRef.current?.click();
  };

  const handleSelectAndUploadFolder = () => {
    handleMenuClose();

    setLoadingFiles(true);
    clearCancellationTimeout();

    // timeout (500ms) to handle cancel
    cancellationTimeoutRef.current = setTimeout(() => {
      setLoadingFiles(false);
      cancellationTimeoutRef.current = null;
    }, 500);

    folderInputRef.current?.click();
  };

  const uploadSelectedFiles = async (selectedFiles: FileWithStatus[]) => {
    if (selectedFiles.length === 0) {
      setError("No files to upload");
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);
    const batchSize = 900;
    const batches = [];

    for (let i = 0; i < selectedFiles.length; i += batchSize) {
      batches.push(selectedFiles.slice(i, i + batchSize));
    }

    let allUploaded: UploadedFile[] = [];
    let allErrors: string[] = [];
    let totalCount = 0;
    let batchResults: UploadResponse[] = [];
    // Use provided uploadId or generate a new one
    const uploadId: string = props.uploadId || uuidv4();
    
    // If this is a newly generated uploadId, notify parent
    if (!props.uploadId && props.onUploadIdCreated) {
      props.onUploadIdCreated(uploadId);
    }
    
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      const formData = new FormData();
      formData.append("upload_id", uploadId);
      batch.forEach(({ file, relativePath }) => {
        formData.append("files", file);
        if (relativePath && relativePath !== file.name) {
          formData.append("paths", relativePath);
        }
      });

      try {
        const response: UploadResponse = await uploadFiles(formData);
        batchResults.push(response);
        allUploaded = allUploaded.concat(response.uploaded || []);
        allErrors = allErrors.concat(response.errors || []);
        totalCount += response.count || 0;
        props.setUploadedFiles(response);

        // Update file statuses for this batch
        setFiles((prev) =>
          prev.map((fileItem) => {
            // Only update files in this batch
            if (!batch.find((b) => b.id === fileItem.id)) return fileItem;
            const uploaded = response.uploaded.find(
              (u: any) => u.filename === fileItem.file.name
            );
            const hasError = response.errors?.some(
              (err) =>
                err.includes(fileItem.file.name) ||
                err.includes(fileItem.relativePath || "")
            );
            if (uploaded) {
              return { ...fileItem, status: "success", error: undefined };
            } else if (hasError) {
              return {
                ...fileItem,
                status: "error",
                error: "Upload failed: server error",
              };
            } else {
              return { ...fileItem, status: "error", error: "Upload failed" };
            }
          })
        );
      } catch (err) {
        // If the entire API call fails for this batch
        const errorMsg =
          err instanceof Error
            ? err.message
            : "A critical upload error occurred";
        allErrors.push(errorMsg);
        setError(errorMsg);
        setFiles((prev) =>
          prev.map((fileItem) => {
            if (!batch.find((b) => b.id === fileItem.id)) return fileItem;
            return {
              ...fileItem,
              status: "error",
              error: "Critical upload failure",
            };
          })
        );
      }
    }
    setUploading(false);
    // Set a combined uploadResult for all batches
    setUploadResult({
      uploaded: allUploaded,
      count: allUploaded.length,
      errors: allErrors.length > 0 ? allErrors : undefined,
    });
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Clear the cancellation timeout immediately because selection was successful
    clearCancellationTimeout();
    setLoadingFiles(false); // hide spinner

    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      event.target.value = "";
      return;
    }

    // Create new session ID for this upload
    const sessionId = uuidv4();
    setCurrentSessionId(sessionId);

    // Mark all previous files as not current session
    setFiles(prev => prev.map(file => ({ ...file, isCurrentSession: false })));

    // Create new files for current session
    const initialFiles: FileWithStatus[] = Array.from(selectedFiles).map(
      (file) => ({
        file,
        status: "uploading",
        relativePath: (file as any).webkitRelativePath || file.name,
        id: generateFileId(file),
        uploadSessionId: sessionId,
        isCurrentSession: true,
      })
    );

    // Add new files to cumulative list
    setFiles(prev => [...prev, ...initialFiles]);
    setUploading(true);
    setError(null);
    setUploadResult(null);
    setDrawerOpen(false);

    // Defer the actual upload call to let the UI update
    setTimeout(async () => {
      await uploadSelectedFiles(initialFiles);
    }, 0);

    // Reset the input so the same files can be selected again
    event.target.value = "";
  };

  const handleFolderSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // 1. Clear the cancellation timeout immediately because selection was successful
    clearCancellationTimeout();
    setLoadingFiles(false); // Hide spinner as soon as event fires

    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      event.target.value = "";
      return;
    }

    // Create new session ID for this upload
    const sessionId = uuidv4();
    setCurrentSessionId(sessionId);

    // Mark all previous files as not current session
    setFiles(prev => prev.map(file => ({ ...file, isCurrentSession: false })));

    // Create new files for current session
    const initialFiles: FileWithStatus[] = Array.from(selectedFiles).map(
      (file) => {
        const webkitPath = (file as any).webkitRelativePath || file.name;
        return {
          file,
          status: "uploading",
          relativePath: webkitPath,
          id: generateFileId(file),
          uploadSessionId: sessionId,
          isCurrentSession: true,
        };
      }
    );

    // Add new files to cumulative list
    setFiles(prev => [...prev, ...initialFiles]);
    setUploading(true); // Show progress/disable button immediately
    setError(null);
    setUploadResult(null);
    setDrawerOpen(false);

    // 3. Defer the actual upload call to let the UI update
    setTimeout(async () => {
      await uploadSelectedFiles(initialFiles);
    }, 0);

    // Reset the input so the same folder can be selected again
    event.target.value = "";
  };

  const handleClear = () => {
    // Only clear current session files, keep previous uploads
    setFiles(prev => prev.filter(file => !file.isCurrentSession));
    setUploadResult(null);
    setError(null);
    setDrawerOpen(false);
    setCurrentSessionId(null);
  };

  const filesWithFinalStatus = files.filter(
    (f) => f.status === "success" || f.status === "error"
  );

  // Helper to get overall status for the alert
  const getOverallStatus = () => {
    if (files.length === 0) return null;
    
    const currentSessionFiles = files.filter(f => f.isCurrentSession);
    const allSuccessCount = files.filter(f => f.status === "success").length;
    const allErrorCount = files.filter(f => f.status === "error").length;
    const uploadingCount = files.filter(f => f.status === "uploading").length;
    
    if (uploadingCount > 0) return "info"; // Still uploading
    if (allErrorCount > 0) return "warning"; // Some errors
    if (allSuccessCount > 0) return "success"; // Has successes
    return "info"; // Default
  };

  const getOverallMessage = () => {
    if (files.length === 0) return "";
    
    const currentSessionFiles = files.filter(f => f.isCurrentSession);
    const allSuccessCount = files.filter(f => f.status === "success").length;
    const allErrorCount = files.filter(f => f.status === "error").length;
    const uploadingCount = files.filter(f => f.status === "uploading").length;
    const currentSessionCount = currentSessionFiles.length;
    
    if (uploadingCount > 0) {
      return `Uploading ${uploadingCount} of ${currentSessionCount} file(s) (${files.length} total)... ${allSuccessCount} completed, ${allErrorCount} failed.`;
    }
    if (uploadResult && currentSessionFiles.length > 0) {
      if (uploadResult.count === currentSessionFiles.length && !(uploadResult.errors && uploadResult.errors.length)) {
        return `Upload complete. Successfully uploaded all ${uploadResult.count} file(s). Total: ${files.length} files.`;
      } else {
        return `Upload complete. Uploaded ${uploadResult.count} out of ${currentSessionFiles.length} file(s). Total: ${files.length} files.${
          uploadResult.errors && uploadResult.errors.length > 0 ? ` ${uploadResult.errors.length} error(s) occurred.` : ''
        }`;
      }
    }
    return `${files.length} file(s) total. ${allSuccessCount} successful, ${allErrorCount} failed, ${uploadingCount} uploading.`;
  };

  return (
    <Box sx={{ mx: "auto", pt: 1, pb: 3 }}>
      <Button
        variant="contained"
        startIcon={<CloudUpload />}
        endIcon={<ArrowDropDown />}
        size="large"
        onClick={handleMenuClick}
        // Disable if files are uploading OR if we are waiting for selection (loadingFiles is true)
        disabled={uploading || loadingFiles}
        sx={{ borderRadius: "99px", textTransform: "none" }}
      >
        {uploading ? "Uploading..." : "Upload Files"}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleSelectAndUploadFiles}>
          <MenuItemIcon>
            <InsertDriveFile fontSize="small" />
          </MenuItemIcon>
          <ListItemText>Upload Files</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSelectAndUploadFolder}>
          <MenuItemIcon>
            <FolderOpen fontSize="small" />
          </MenuItemIcon>
          <ListItemText>Upload Folder</ListItemText>
        </MenuItem>
      </Menu>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        onChange={handleFileSelect}
      />
      {/* Hidden folder inputs */}
      <input
        ref={folderInputRef}
        type="file"
        hidden
        /* @ts-ignore */
        webkitdirectory="true"
        directory="true"
        multiple
        onChange={handleFolderSelect}
      />
      {/* temporary progress until the browser finishes processing files after selection */}
      {loadingFiles && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 2,
          }}
        >
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body1">Loading files...</Typography>
        </Box>
      )}

      {/* Progress Bar */}
      {uploading && (
        <Box sx={{ my: 3 }}>
          <LinearProgress variant="indeterminate" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Uploading {files.length} file{files.length !== 1 ? "s" : ""}...
          </Typography>
        </Box>
      )}

      {/* Error Alert (Critical Failure) */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success/completion Alert with Drawer - Show immediately when files are selected */}
      {files.length > 0 && (
        <Alert
          severity={getOverallStatus() || "info"}
          sx={{ mt: 2 }}
          action={
            <>
              <Button
                color="inherit"
                size="small"
                onClick={() => setDrawerOpen(true)}
                startIcon={<Visibility />}
              >
                View Status ({files.length})
              </Button>
              <IconButton onClick={handleClear} sx={{ ml: 3 }}>
                <CloseIcon />
              </IconButton>
            </>
          }
        >
          {getOverallMessage()}
        </Alert>
      )}

      {/* Status Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        
        PaperProps={{
          sx: { width: { xs: "100%", sm: 450 } },
        }}
      >
        <Box sx={{ pt: 12, px: 2, pb: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Upload Status Details</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => {
                  setFiles([]);
                  setUploadResult(null);
                  setError(null);
                  setCurrentSessionId(null);
                }}
              >
                Clear All
              </Button>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Divider sx={{ mb: 2 }} />

          <List dense>
            {files.length > 0 ? (
              (() => {
                // Group files by session (current session first, then by session ID)
                const currentSessionFiles = files.filter(f => f.isCurrentSession);
                const previousSessionFiles = files.filter(f => !f.isCurrentSession);
                
                return (
                  <>
                    {/* Current Session */}
                    {currentSessionFiles.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                          Current Upload ({currentSessionFiles.length} files)
                        </Typography>
                        {currentSessionFiles.map((fileItem) => (
                          <ListItem
                            key={fileItem.id}
                            sx={{
                              borderRadius: 1,
                              borderLeft: '3px solid',
                              borderLeftColor: 'primary.main',
                              ml: 1,
                            }}
                          >
                            <ListItemIcon>
                              {fileItem.status === "success" ? (
                                <CheckCircle color="success" />
                              ) : fileItem.status === "error" ? (
                                <ErrorIcon color="error" />
                              ) : fileItem.status === "uploading" ? (
                                <CircularProgress size={20} />
                              ) : (
                                <InsertDriveFile color="action" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={fileItem.file.name}
                              secondary={
                                <Stack direction="column" spacing={0.5}>
                                  <Typography variant="caption" display="block">
                                    Status: {fileItem.status.toUpperCase()}
                                  </Typography>
                                  {fileItem.relativePath && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Path: {fileItem.relativePath}
                                    </Typography>
                                  )}
                                  {fileItem.error && (
                                    <Chip
                                      label={fileItem.error}
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ width: "fit-content", mt: 0.5 }}
                                    />
                                  )}
                                </Stack>
                              }
                            />
                          </ListItem>
                        ))}
                        <Divider sx={{ my: 1 }} />
                      </>
                    )}
                    
                    {/* Previous Sessions */}
                    {previousSessionFiles.length > 0 && (
                      <>
                        <Typography variant="subtitle2" sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                          Previous Uploads ({previousSessionFiles.length} files)
                        </Typography>
                        {previousSessionFiles.map((fileItem) => (
                          <ListItem
                            key={fileItem.id}
                            sx={{
                              borderRadius: 1,
                              opacity: 0.8,
                            }}
                          >
                            <ListItemIcon>
                              {fileItem.status === "success" ? (
                                <CheckCircle color="success" />
                              ) : fileItem.status === "error" ? (
                                <ErrorIcon color="error" />
                              ) : fileItem.status === "uploading" ? (
                                <CircularProgress size={20} />
                              ) : (
                                <InsertDriveFile color="action" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={fileItem.file.name}
                              secondary={
                                <Stack direction="column" spacing={0.5}>
                                  <Typography variant="caption" display="block">
                                    Status: {fileItem.status.toUpperCase()}
                                  </Typography>
                                  {fileItem.relativePath && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Path: {fileItem.relativePath}
                                    </Typography>
                                  )}
                                  {fileItem.error && (
                                    <Chip
                                      label={fileItem.error}
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ width: "fit-content", mt: 0.5 }}
                                    />
                                  )}
                                </Stack>
                              }
                            />
                          </ListItem>
                        ))}
                      </>
                    )}
                  </>
                );
              })()
            ) : (
              <Typography variant="body2" color="text.secondary">
                No files selected yet. Select files to upload.
              </Typography>
            )}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default RAGFileUploader;
